import React, { useState } from 'react';
import { Lock, Menu, Facebook, Twitter, Linkedin, Instagram, MapPin, Phone, Mail, ShieldCheck, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

export const PublicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [exploreDropdownOpen, setExploreDropdownOpen] = useState(false);
  const [individualTrainingDropdownOpen, setIndividualTrainingDropdownOpen] = useState(false);
  const [corporateTrainingDropdownOpen, setCorporateTrainingDropdownOpen] = useState(false);
  const base = ('/verify/').replace(/\/$/, '');
  const relPath = location.pathname.startsWith(base)
    ? location.pathname.slice(base.length) || '/'
    : location.pathname;
  const isAuthPage = relPath === '/login' || relPath === '/signup';
  const isLandingPage = relPath === '/';

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 font-sans">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo area */}
            <div className="flex-shrink-0 flex items-center gap-2 cursor-pointer">
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src={`/verify/logo.png`} 
                  alt="Speedlink Logo" 
                  className="w-15 h-10"
                />
                <div className="flex flex-col">
                  
                </div> 
              </Link>
            </div>

            {/* Desktop Nav - Only show on Landing Page */}
            {isLandingPage && (
              <nav className="hidden md:flex space-x-8 text-sm font-semibold text-gray-700 relative">
                <div className="relative group">
                  <button 
                    onClick={() => setExploreDropdownOpen(!exploreDropdownOpen)}
                    className="hover:text-primary transition-colors flex items-center gap-1"
                  >
                    Explore <ChevronDown className="w-4 h-4" />
                  </button>
                  {exploreDropdownOpen && (
                    <div className="fixed left-0 right-0 top-20 w-screen bg-white shadow-2xl border-b border-gray-100 z-50 p-8">
                      <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-4 gap-8">
                          {/* Column 1: In Demand Courses */}
                          <div>
                            <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wider">In Demand Courses</h4>
                            <ul className="space-y-3 text-sm text-gray-700">
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Microsoft Power BI Data Analyst CertPREP Course</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">CompTIA Security+ (SY0-701) CertPREP Course</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Advanced WordPress Training</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">UI/UX Design Training</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Wireless Networking Training</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Smart Surveillance & Cloud IP Telephony Training</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Fibre Optics Engineering with Field Internships</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Multimedia Production, Videography & Live Streaming</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">AWS Cloud Practitioner & Solutions Architect Training</a></li>
                            </ul>
                          </div>
                          
                          {/* Column 2: Career Roles & Pathways */}
                          <div>
                            <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wider">Career Roles & Pathways</h4>
                            <ul className="space-y-3 text-sm text-gray-700">
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">CompTIA IT Fundamentals+ (FC0-U61) Cert Prep Course</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Certified Ethical Hacker (CEH) CertPREP Course</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Developing Solutions for Microsoft Azure</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">CompTIA Advanced Security Practitioner (CASP+ CAS-004) CertPrep Course</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Cisco Certified Network Associate (CCNA) CertPREP Course</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Microsoft Azure Administrator</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">AWS Certified Solutions Architect – Associate (SAA-C02) CertPREP Course</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Microsoft Power BI Data Analyst CertPREP Course</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">AWS Certified Developer Associate (DVA-C02)</a></li>
                            </ul>
                          </div>
                          
                          {/* Column 3: Courses for Beginners */}
                          <div>
                            <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wider">Courses for Beginners</h4>
                            <ul className="space-y-3 text-sm text-gray-700">
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Wireless Networking Training</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">UI/UX Design Training</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">The Complete Digital Marketing Guide</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Smart Surveillance & Cloud IP Telephony Training</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Multimedia Production, Videography & Live Streaming</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Graphics and Product Design</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Full Stack Web Development</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Fibre Optics Engineering with Field Internships</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Embedded Systems, Robotics, and AI</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Data Analytics</a></li>
                            </ul>
                          </div>
                          
                          {/* Column 4: Self Paced Courses */}
                          <div>
                            <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wider">Self Paced Courses</h4>
                            <ul className="space-y-3 text-sm text-gray-700">
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">CompTIA IT Fundamentals+ (FC0-U61) Cert Prep Course</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">AWS Cloud Practitioner & Solutions Architect Training</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">CompTIA+ A+ Core 1 (220-1101) CertPREP Course</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Microsoft Azure Data Fundamentals (DP-900) CertPREP Course</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Certified Information Systems Security Professional (CISSP) CertPREP Course</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Microsoft Azure Administrator</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">AWS Certified Solutions Architect – Associate (SAA-C02) CertPREP Course</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">AWS Certified SysOps Administrator Associate (SOA-C02) CertPREP Course</a></li>
                              <li><a href="#" className="hover:text-primary hover:font-semibold transition-colors">Cisco Certified Network Associate (CCNA)</a></li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative group">
                  <button 
                    onClick={() => setIndividualTrainingDropdownOpen(!individualTrainingDropdownOpen)}
                    className="hover:text-primary transition-colors flex items-center gap-1"
                  >
                    Individual Training <ChevronDown className="w-4 h-4" />
                  </button>
                  {individualTrainingDropdownOpen && (
                    <div className="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-2xl border border-gray-100 z-50 p-6">
                      <ul className="space-y-4">
                        <li>
                          <a href="#" className="flex items-start gap-3 p-3 hover:bg-indigo-50 rounded-lg transition-colors group/item">
                            <div className="flex-grow">
                              <h5 className="font-bold text-gray-900 group-hover/item:text-primary transition-colors">Tutor Led</h5>
                              <p className="text-xs text-gray-600 mt-1">Interactive sessions led by certified experts. Learn in real-time through virtual or on-site classes.</p>
                            </div>
                          </a>
                        </li>
                        <li>
                          <a href="#" className="flex items-start gap-3 p-3 hover:bg-indigo-50 rounded-lg transition-colors group/item">
                            <div className="flex-grow">
                              <h5 className="font-bold text-gray-900 group-hover/item:text-primary transition-colors">Self Paced</h5>
                              <p className="text-xs text-gray-600 mt-1">Access expertly curated course materials and learn at your own pace with flexible scheduling.</p>
                            </div>
                          </a>
                        </li>
                        <li>
                          <a href="#" className="flex items-start gap-3 p-3 hover:bg-indigo-50 rounded-lg transition-colors group/item">
                            <div className="flex-grow">
                              <h5 className="font-bold text-gray-900 group-hover/item:text-primary transition-colors">SIWES Placement & Industrial Training</h5>
                              <p className="text-xs text-gray-600 mt-1">Gain real-world experience through structured internships and industrial training programs.</p>
                            </div>
                          </a>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
                <div className="relative group">
                  <button 
                    onClick={() => setCorporateTrainingDropdownOpen(!corporateTrainingDropdownOpen)}
                    className="hover:text-primary transition-colors flex items-center gap-1"
                  >
                    Corporate Training <ChevronDown className="w-4 h-4" />
                  </button>
                  {corporateTrainingDropdownOpen && (
                    <div className="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-2xl border border-gray-100 z-50 p-6">
                      <ul className="space-y-4">
                        <li>
                          <a href="#" className="flex items-start gap-3 p-3 hover:bg-indigo-50 rounded-lg transition-colors group/item">
                            <div className="flex-grow">
                              <h5 className="font-bold text-gray-900 group-hover/item:text-primary transition-colors">For Government Agencies</h5>
                              <p className="text-xs text-gray-600 mt-1">Customized training solutions tailored for government organizations and agencies.</p>
                            </div>
                          </a>
                        </li>
                        <li>
                          <a href="#" className="flex items-start gap-3 p-3 hover:bg-indigo-50 rounded-lg transition-colors group/item">
                            <div className="flex-grow">
                              <h5 className="font-bold text-gray-900 group-hover/item:text-primary transition-colors">For Private Companies</h5>
                              <p className="text-xs text-gray-600 mt-1">Scalable training programs designed for corporate teams and enterprises.</p>
                            </div>
                          </a>
                        </li>
                        <li>
                          <a href="#" className="flex items-start gap-3 p-3 hover:bg-indigo-50 rounded-lg transition-colors group/item">
                            <div className="flex-grow">
                              <h5 className="font-bold text-gray-900 group-hover/item:text-primary transition-colors">For NGOs & Educational Institutions</h5>
                              <p className="text-xs text-gray-600 mt-1">Specialized programs for non-profits, schools, and universities.</p>
                            </div>
                          </a>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
                <a href="https://speedlinktraining.com/certify/" className="hover:text-primary transition-colors">Certification</a>
                <a href="https://speedlinktraining.com/blog/" className="hover:text-primary transition-colors">Blog</a>
                <a href="#about" className="hover:text-primary transition-colors">About us</a>
              </nav>
            )}

            {/* Right Actions */}
            <div className="flex items-center space-x-4">
              {isAuthPage ? (
                <Link to="/" className="flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-100 rounded-lg text-sm font-bold text-primary hover:bg-indigo-100 transition-colors">
                  <ShieldCheck className="w-4 h-4" />
                  Verify Certificate
                </Link>
              ) : (
                 <Link to="/login" className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors">
                  <Lock className="w-4 h-4" />
                  Login/Signup
                </Link>
              )}
              <button className="md:hidden text-gray-700">
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      {!isAuthPage && (
        <footer className="bg-primary text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
              {/* Company Info */}
              <div className="space-y-4">
                 <div className="flex items-center gap-2 mb-4">
                  <img 
                    src={'/verify/logo.png'} 
                    alt="Speedlink Logo" 
                    className="w-15 h-10 "
                  />
                  
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Empowering the next generation of tech leaders through world-class training and innovative solutions.
                </p>
                <div className="flex space-x-4 pt-2">
                  <a href="#" className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors">
                    <Facebook className="w-4 h-4 text-white" />
                  </a>
                  <a href="#" className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors">
                    <Twitter className="w-4 h-4 text-white" />
                  </a>
                  <a href="#" className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors">
                    <Linkedin className="w-4 h-4 text-white" />
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="text-lg font-bold mb-6 border-b border-indigo-800 pb-2 inline-block">Quick Links</h3>
                <ul className="space-y-3 text-sm text-gray-300">
                  <li><a href="#" className="hover:text-white transition-colors flex items-center gap-2">About us</a></li>
                  <li><a href="#" className="hover:text-white transition-colors flex items-center gap-2">Corporate Training</a></li>
                  <li><a href="#" className="hover:text-white transition-colors flex items-center gap-2">Individual Training</a></li>
                  <li><a href="#" className="hover:text-white transition-colors flex items-center gap-2">Certification</a></li>
                  <li><a href="#" className="hover:text-white transition-colors flex items-center gap-2">Blog</a></li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                 <h3 className="text-lg font-bold mb-6 border-b border-indigo-800 pb-2 inline-block">Contact Us</h3>
                 <ul className="space-y-4 text-sm text-gray-300">
                   <li className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>No 15 Road 6, Okey Wali Estate,<br/>Rumalogu opposite Alakahia Car Wash,<br/>Port Harcourt, Rivers State.</span>
                   </li>
                   <li className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                      <span>+234 800 123 4567</span>
                   </li>
                   <li className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                      <span>info@speedlinkng.com</span>
                   </li>
                 </ul>
              </div>

              {/* Map/Extra (Placeholder) */}
              <div className="bg-white/5 rounded-lg p-4 flex items-center justify-center border border-white/10">
                  <div className="text-center">
                    <p className="text-sm font-bold text-white mb-2">Locate Us</p>
                    <p className="text-xs text-gray-300">Find us on Google Maps for easy navigation to our training center.</p>
                  </div>
              </div>
            </div>

            <div className="border-t border-indigo-900 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-400">© 2025 Speedlink Hi-Tech Solutions Limited. All rights reserved.</p>
              <div className="flex gap-6 text-sm text-gray-400">
                <a href="#" className="hover:text-white">Privacy Policy</a>
                <a href="#" className="hover:text-white">Terms of Service</a>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};
